/**
 * Brand Showcase – Scroll-Triggered Split Reveal Animation
 *
 * Desktop:
 *   - Media side sweeps in from its layout direction (left or right)
 *   - Content elements (logo → description → CTA) cascade in with stagger
 * Mobile:
 *   - Both sides slide up from below (stacked layout)
 *
 * Fires once when the section scrolls into view.
 * Respects prefers-reduced-motion, includes GSAP polling + fallback timer.
 */

class BrandShowcaseReveal extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFallback();
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — reveal after 5 s if GSAP never loads */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  /* -------- GSAP helpers -------- */

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  /* -------- Initialisation -------- */

  async init() {
    await this.waitForGSAP();
    var gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* Cancel safety fallback */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('BrandShowcase-' + sectionId);
    if (!section) return;

    /* Detect layout direction to determine which way the image slides in */
    var isImageRight = section.classList.contains('brand-showcase--image-right');
    var isMobile = window.innerWidth < 750;

    /* Gather targets */
    var media = section.querySelector('.brand-showcase__media');
    var logo = section.querySelector('.brand-showcase__logo');
    var description = section.querySelector('.brand-showcase__description');
    var cta = section.querySelector('.brand-showcase__cta');

    if (!media && !logo && !description && !cta) {
      this.showFallback();
      return;
    }

    /* Register ScrollTrigger */
    var hasScrollTrigger = typeof ScrollTrigger !== 'undefined' ||
                           (gsapLib.plugins && gsapLib.plugins.scrollTrigger);

    if (hasScrollTrigger && gsapLib.registerPlugin) {
      try {
        gsapLib.registerPlugin(ScrollTrigger || gsapLib.plugins.scrollTrigger);
      } catch (e) {
        // Already registered
      }
    }

    /* Build timeline */
    var triggerEl = section.querySelector('.brand-showcase__container') || section;

    if (hasScrollTrigger) {
      this.timeline = gsapLib.timeline({
        scrollTrigger: {
          trigger: triggerEl,
          start: 'top 80%',
          once: true
        }
      });
    } else {
      /* Fallback: IntersectionObserver */
      this.timeline = gsapLib.timeline({ paused: true });

      var self = this;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.timeline.play();
            observer.disconnect();
          }
        });
      }, { threshold: 0.15 });
      observer.observe(triggerEl);
    }

    /* 1. Media side — sweep in from its layout direction */
    if (media) {
      if (isMobile) {
        /* Mobile: slide up from below */
        this.timeline.fromTo(media,
          { opacity: 0, y: 80 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          0
        );
      } else {
        /* Desktop: slide from the image's side */
        var xOffset = isImageRight ? 80 : -80;
        this.timeline.fromTo(media,
          { opacity: 0, x: xOffset },
          { opacity: 1, x: 0, duration: 0.9, ease: 'power3.out' },
          0
        );
      }
    }

    /* 2. Content elements — cascade in */
    var contentDelay = 0.2;

    if (logo) {
      this.timeline.fromTo(logo,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        contentDelay
      );
    }

    if (description) {
      this.timeline.fromTo(description,
        { opacity: 0, y: 25 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        contentDelay + 0.12
      );
    }

    if (cta) {
      this.timeline.fromTo(cta,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.4)' },
        contentDelay + 0.24
      );
    }
  }

  /* -------- Fallback -------- */

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('BrandShowcase-' + sectionId);
    if (!section) return;

    var els = section.querySelectorAll(
      '.brand-showcase__media, .brand-showcase__logo, .brand-showcase__description, .brand-showcase__cta'
    );
    els.forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /* -------- Cleanup -------- */

  disconnectedCallback() {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      if (this.timeline.kill) this.timeline.kill();
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }
}

if (!customElements.get('brand-showcase-reveal')) {
  customElements.define('brand-showcase-reveal', BrandShowcaseReveal);
}


